/**
 * useUserId Hook
 *
 * Returns the current user's ID according to our Custom UUID Architecture.
 * This hook abstracts away the complexity of the dual-ID system.
 *
 * CRITICAL: Always use this hook instead of accessing user.id directly!
 *
 * Architecture:
 * - Guest users: Returns guestSession.id (custom UUID in user_profiles)
 * - Authenticated users: Returns profile.user_id (custom UUID in user_profiles)
 * - NEVER returns auth.users.id (Supabase auth ID) - that's for authentication only
 *
 * All database foreign keys reference user_profiles.user_id, not auth.users.id.
 *
 * @returns {string | null} The current user's custom UUID, or null if not logged in
 *
 * @example
 * ```typescript
 * const userId = useUserId()
 *
 * if (userId) {
 *   // Use userId for API calls, database queries, etc.
 *   await mealPlanningService.createSession(userId, ...)
 * }
 * ```
 */

import { useAuth } from '../contexts/AuthContext'

export function useUserId(): string | null {
  const { profile, guestSession, isAuthenticated, isGuest } = useAuth()

  // Authenticated user: Use profile.userId (custom UUID - camelCase from API)
  if (isAuthenticated && profile?.userId) {
    return profile.userId
  }

  // Guest user: Use guestSession.id (custom UUID)
  if (isGuest && guestSession?.id) {
    return guestSession.id
  }

  // No user logged in
  return null
}

/**
 * Hook to get detailed user ID information (for debugging)
 *
 * @returns Object with all ID-related information
 *
 * @example
 * ```typescript
 * const { userId, supabaseUserId, isGuest, source } = useUserIdDebug()
 * console.log('Using userId:', userId, 'from', source)
 * ```
 */
export function useUserIdDebug() {
  const { user, profile, guestSession, isAuthenticated, isGuest } = useAuth()

  return {
    userId: profile?.userId || guestSession?.id || null,
    supabaseUserId: user?.id || null,
    profileUserId: profile?.userId || null,
    guestSessionId: guestSession?.id || null,
    isAuthenticated,
    isGuest,
    source: isAuthenticated ? 'profile.userId' : isGuest ? 'guestSession.id' : 'none'
  }
}
